import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { Color } from 'module/ui/components/styles/colors';
import { WarningAltFilled } from '@carbon/icons-react';
import { faChevronDown, faRedo } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { Menu, Skeleton, Tooltip } from 'antd';
import { PageType } from 'api/page-template/types';
import { useGetAllMeetingsQuery, useSelectedMeetingId } from 'queries/meeting';
import { useGetSectionPreviewQuery } from 'queries/templates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  FlexWrapper,
  FlexWrapperWithGutter,
  FlexWrapperWithGutterSize,
} from 'components/common/styles';
import { DocumentEditor } from 'components/organisms/document-editor/document-editor';

const MeetingListDropdownWrapper = styled.div`
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-03);
  max-width: 500px;
  width: 100%;
  height: 32px;
  border: 1px solid var(--scarlet-gray-40);
  color: var(--text-placeholder);
`;

const MeetingTypeDropdownWrapper = styled.div`
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-03);
  height: 32px;
  gap: var(--spacing-03);
  border: 1px solid var(--scarlet-gray-40);
  color: var(--text-primary);
`;

const StyledMenu = styled(Menu)`
  .ant-dropdown-menu-item {
    padding: var(--spacing-10);
  }
`;

const PreviewContainer = styled.div`
  padding: var(--spacing-08) var(--spacing-10);
  width: 100%;
`;

const EllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 380px;
  color: var(--text-primary);
`;

const DropDownLabel = styled.div`
  flex: 1;
`;

const DropDownIconWrapper = styled.div`
  cursor: pointer;
  color: var(--text-secondary);
`;

const AlertWrapper = styled.div<{ color: Color }>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: var(--spacing-05) 0;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    fill: var(--red-50);
  }
`;

function MeetingTemplatePagePreview({
  showPreview,
  setShowPreview,
  getPayload,
}: {
  showPreview: boolean;
  setShowPreview: (showPreview: boolean) => void;
  getPayload: () => object;
}) {
  const { selectedMeetingId } = useSelectedMeetingId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    data: sectionPreview,
    isLoading: isSectionPreviewLoading,
    isFetching: isSectionPreviewFetching,
    error: sectionPreviewError,
    refetch: refetchSectionPreview,
  } = useGetSectionPreviewQuery({
    meetingId: selectedMeetingId ?? 0,
    pageType: PageType.Meeting,
    payload: getPayload(),
    enabled: selectedMeetingId ? true : false,
  });

  useEffect(() => {
    if (selectedMeetingId) {
      refetchSectionPreview();
    }
  }, [selectedMeetingId, refetchSectionPreview]);

  const [searchQuery, setSearchQuery] = useState('');

  const { data: meetings, refetch } = useGetAllMeetingsQuery();

  /*useGetPaginatedMeetingsQuery({
    enabled: false,
    page: 1,
    limit: 10,
    filters: {
      participantIds: 1,
      searchQuery: searchQuery,
      companyIds: 2,
    },
  });*/

  const fetchMeetings = useCallback(
    (open: boolean) => {
      setIsDropdownOpen(open);
      if (open) {
        refetch();
      }
    },
    [refetch],
  );

  const accountsMenu = useMemo(() => {
    const menuItems = [
      {
        label: 'All accounts',
        key: 'all-meetings',
      },
      {
        label: 'My meetings',
        key: 'my-meetings',
      },
    ];

    return menuItems;
  }, []);

  const selectedMeeting = meetings?.find(
    (m) => m.meetingId === selectedMeetingId,
  );

  const editorExtensionsConfig: Partial<any> = {
    tableToolbarConfig: {
      enabled: false,
    },
  };

  return (
    <CustomLayoutDrawer
      open={showPreview}
      onClose={() => setShowPreview(false)}
      width={900}
      {...DefaultCustomLayoutDrawerProps}
      closeIcon={<></>}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title
          breadcrumbs={
            <DrawerBreadCrumbs breadcrumbs={[{ title: 'Section preview' }]} />
          }
        >
          <div>Test</div>
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <FlexWrapperWithGutter>
            <Dropdown
              trigger={['click']}
              onOpenChange={fetchMeetings}
              open={isDropdownOpen}
              overlay={
                <StyledMenu>
                  <SearchInputExpanded
                    placeholder="Search meetings"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    onClear={() => {
                      setSearchQuery('');
                    }}
                  />
                </StyledMenu>
              }
            >
              <MeetingListDropdownWrapper>
                <div style={{ flex: 1 }}>
                  {selectedMeeting ? (
                    <EllipsisText>{selectedMeeting.meetingName}</EllipsisText>
                  ) : (
                    <FormattedMessage id="preview.meetings" />
                  )}
                </div>
                <div style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </div>
              </MeetingListDropdownWrapper>
            </Dropdown>
            {/*<MeetingPreviewDropdown
              label="All meetings"
              menuItems={meetingsMenu}
            /> */}
            <MeetingPreviewDropdown
              label="All accounts"
              menuItems={accountsMenu}
            />
            <Tooltip title="Refresh">
              <Button
                variant="secondary"
                onClick={() => refetchSectionPreview()}
                className="w-8 h-8 border-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faRedo} />
              </Button>
            </Tooltip>
          </FlexWrapperWithGutter>
          <FlexWrapper>
            <PreviewContainer>
              {sectionPreviewError && (
                <div className="mb-6">
                  <AlertWrapper color="red">
                    <WarningAltFilled size={20} />
                    {sectionPreviewError.message}
                  </AlertWrapper>
                </div>
              )}
              {isSectionPreviewLoading || isSectionPreviewFetching ? (
                <div style={{ paddingTop: 'var(--spacing-06)', width: '100%' }}>
                  <Skeleton active />
                </div>
              ) : (
                sectionPreview && (
                  <div>
                    <DocumentEditor
                      value={sectionPreview.finalContent}
                      readonly={true}
                      extensionConfig={editorExtensionsConfig}
                    />
                  </div>
                )
              )}
            </PreviewContainer>
          </FlexWrapper>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer $rightAlignedAction>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button variant="primary" onClick={() => setShowPreview(false)}>
            <FormattedMessage id="done" />
          </Button>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
}

const MeetingPreviewDropdown = function ({
  menuItems,
  label,
}: {
  menuItems: { label: string; key: string }[];
  label: string;
}) {
  return (
    <Dropdown trigger={['click']} menu={{ items: menuItems }}>
      <MeetingTypeDropdownWrapper>
        <FlexWrapperWithGutterSize size="5">
          <DropDownLabel>{label}</DropDownLabel>
          <DropDownIconWrapper>
            <FontAwesomeIcon icon={faChevronDown} />
          </DropDownIconWrapper>
        </FlexWrapperWithGutterSize>
      </MeetingTypeDropdownWrapper>
    </Dropdown>
  );
};

export { MeetingTemplatePagePreview };
